import React from 'react';
import { Moon, Sun } from 'lucide-react';
import { useDarkMode } from '../../context/DarkModeContext';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';
import { ProfilePictureUpload } from '../profile/ProfilePictureUpload';
import { PMT2PathTestSection } from '../admin/PMT2PathTestSection';

interface SettingsContentProps {
  showProfilePicture?: boolean;
}

/**
 * Reusable Settings content component that can be used across all apps
 * @param showProfilePicture Whether to show the profile picture upload section (default: true)
 */
export const SettingsContent: React.FC<SettingsContentProps> = ({
  showProfilePicture = true
}) => {
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  const { userEmail } = useAuth();

  // Check if user is admin (you can modify this logic as needed)
  const isAdmin = userEmail?.includes('@jlsapps.com');

  return (
    <>
      {/* Profile Picture Upload */}
      {showProfilePicture && <ProfilePictureUpload />}
      
      {/* Appearance Settings */}
      <Card className={`${showProfilePicture ? 'mt-6' : ''} mb-6 dark:bg-gray-800 dark:border-gray-700`}>
        <CardHeader>
          <CardTitle className="text-gray-900 dark:text-white">Appearance</CardTitle>
          <CardDescription className="dark:text-gray-400">
            Customize how the application looks and feels
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {isDarkMode ? (
                <Moon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              ) : (
                <Sun className="h-5 w-5 text-gray-500" />
              )}
              <span className="text-sm font-medium text-gray-900 dark:text-white">Dark Mode</span>
            </div>
            <Switch
              checked={isDarkMode}
              onCheckedChange={toggleDarkMode}
              className="data-[state=checked]:bg-blue-600"
            />
          </div>
        </CardContent>
      </Card>

      {/* PMT2 Path Testing - Admin Only */}
      {isAdmin && (
        <div className="mt-6">
          <PMT2PathTestSection />
        </div>
      )}
    </>
  );
};

export default SettingsContent;
